import {
    DEFAULT_CURRENCY_FORMAT,
    formatMajorMoney,
    formatNumber,
} from '@eventbrite/intl';
import find from 'lodash/find';
import isArray from 'lodash/isArray';
import { isSubmitting } from 'redux-form';
import { PromoCodeOptions } from '../components/promoCode/constants';
import {
    PROMO_CODE_FAILURE_MESSAGE,
    PROMO_CODE_FORM_NAME,
    PROMO_CODE_TYPES,
} from '../constants';
import { Ticket, TicketVariant } from '../models/tickets';
import { CheckoutState } from '../state';

export const isTicketWithPromoCode = (ticket: TicketVariant) =>
    ticket.code && ticket.discountType !== PROMO_CODE_TYPES.PUBLIC;
/**
 * Returns the first variant with a code, or an empty object
 *
 * @param variants
 */
export const getFirstVariantTicketWithPromoCode = (
    variants: Array<TicketVariant> | Record<string, Ticket>,
): TicketVariant | {} => find(variants, isTicketWithPromoCode) || {};

/**
 * getVariantPromoCode
 *
 * NOTE: Currently only one promotional code can be applied per order.
 * We may have multiple promo codes per order in the future.
 *
 * @param tickets the flattened list of Tickets (Array) or an Object of tickets.
 * @returns promotional code
 */
export const getVariantPromoCode = (
    tickets: Array<Ticket> | Record<string, Ticket>,
) => {
    const ticketsAsArray = isArray(tickets) ? tickets : Object.values(tickets);
    const promoCodes = ticketsAsArray.map((ticket) => {
        const ticketVariant = getFirstVariantTicketWithPromoCode(
            ticket.variants,
        );
        return 'code' in ticketVariant ? ticketVariant.code : null;
    });

    return find(promoCodes, (code) => !!code);
};

/**
 * getPromoCodeInformation
 *
 * For either an Array or an Object of tickets, find a ticket with promo code applied, and
 * return an Object of promo code-related information (i.e. discountType, code, percentOff,
 * amountOff, originalCost, etc.).
 *
 * @param tickets the flattened list of Tickets (Array) or an Object of tickets.
 * @returns promotional-code-related information
 */
export const getPromoCodeInformation = (
    tickets: Array<Ticket> | Record<string, Ticket>,
): TicketVariant | {} => {
    const ticketWithPromo = find(tickets, (ticket: Ticket) => {
        const firstVariantTicketWithPromoCode =
            getFirstVariantTicketWithPromoCode(ticket.variants);

        return !!(
            'code' in firstVariantTicketWithPromoCode &&
            firstVariantTicketWithPromoCode.code
        );
    }) as Ticket | undefined; // `find` is pooly typed and cannot infer the actual return type

    return ticketWithPromo
        ? getFirstVariantTicketWithPromoCode(ticketWithPromo.variants)
        : {};
};

const _isPromoCodeAvailableForDiscount = (promoCodeType?: string) =>
    promoCodeType === PROMO_CODE_TYPES.DISCOUNT ||
    promoCodeType === PROMO_CODE_TYPES.HOLD;

const _isShouldShowPromoCodeLinkInState = (state: CheckoutState) => {
    return (
        'shouldShowPromoCodeLink' in state.event &&
        state.event.shouldShowPromoCodeLink
    );
};
const _getShouldShowPromoCodeForm = (state: CheckoutState) => {
    return _isShouldShowPromoCodeLinkInState(state);
};

export const getPromoCodeFormProps = (
    state: CheckoutState,
): {
    shouldShowPromoCodeForm: boolean;
    promoCodeFormOptions: PromoCodeOptions;
} => {
    const { ticketsById } = state.tickets;
    const isLoading = isSubmitting(PROMO_CODE_FORM_NAME)(state);
    const promoInformation = getPromoCodeInformation(ticketsById);
    const discountDetails: {
        percentOff?: string;
        amountOff?: string;
    } = {};
    const { currencyFormat = DEFAULT_CURRENCY_FORMAT } = state.app || {};
    let promoCodeType;
    let promoCode;

    if ('code' in promoInformation) {
        promoCodeType = promoInformation.discountType;
        promoCode = promoInformation.code;

        // Additional information related to promo codes that
        // apply discounts to ticket(s)
        if (_isPromoCodeAvailableForDiscount(promoCodeType)) {
            const { percentOff, amountOff } = promoInformation;

            if (percentOff) {
                discountDetails.percentOff = formatNumber(
                    parseFloat(percentOff),
                    currencyFormat,
                );
            }

            if (amountOff) {
                const { value, currency } = amountOff;

                discountDetails.amountOff = formatMajorMoney(value, currency);
            }
        }
    }

    const shouldShowPromoCodeForm = _getShouldShowPromoCodeForm(state);

    return {
        shouldShowPromoCodeForm,
        promoCodeFormOptions: {
            promoCode,
            isLoading,
            promoCodeType,
            ...discountDetails,
        },
    };
};

export const getPromoCodeErrorMessage = () => PROMO_CODE_FAILURE_MESSAGE;
